import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { leagueSlugFromPath } from "../../utils/leaguePath";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { stockSearch } from "../../api/user";
import { baseSymbol, instrumentName } from "../../utils/instrumentFormat";
import useLeagueContext from "../../utils/useLeagueContext";
import { isEligibleInLeague, leagueSearchTabs } from "../../utils/leagueAssets";

// Rank merged (stocks + crypto) results by how closely they match the query, so
// the best matches lead. Lower score = better. Stable within a score band.
function rankByMatch(list, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return list;
  const score = (item) => {
    const sym = baseSymbol(item.ticker || item.symbol).toLowerCase();
    const name = String(item.name || "").toLowerCase();
    if (sym === q) return 0; // exact ticker
    if (sym.startsWith(q)) return 1; // ticker prefix
    if (name.startsWith(q)) return 2; // name prefix
    if (name.split(/[\s\-/]+/).some((w) => w.startsWith(q))) return 3; // any word prefix
    if (name.includes(q)) return 4; // name contains
    if (sym.includes(q)) return 5; // ticker contains
    return 6;
  };
  return list
    .map((item, i) => ({ item, i, s: score(item) }))
    .sort((a, b) => a.s - b.s || a.i - b.i)
    .map((x) => x.item);
}

// Mobile chart-screen search. One low-profile input, NO category pill — it
// queries Stocks AND Crypto together and merges the results. Picking a result
// opens that asset's chart (navigates to /chartSearch/:symbol, the same route
// the watchlist "open chart" uses) WITHOUT adding it to any watchlist, mirroring
// the desktop symbol search.
export default function ChartMobileSearch({ onClose }) {
  // The league we are standing in, read off the URL through the shared store —
  // no extra request, and null on the main Trade screen.
  //
  // Client, "INSIDE A LEAGUE": the mobile magnifying glass "lets you bring up an
  // ineligible asset. Load the chart, try to buy it, and then you get an error."
  //
  // This screen has no category pill to hide — it queries Stocks and Crypto
  // together and merges. So "only the tabs the organizer chooses" becomes "only
  // the MARKETS the organizer chose are queried", which also fixes a second
  // thing: Forex was never searched here at all, so a forex league's search
  // could only ever come back empty.
  const { game } = useLeagueContext();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const controllerRef = useRef(null);
  const reqRef = useRef(0);

  useEffect(() => {
    inputRef.current?.focus();
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (controllerRef.current) controllerRef.current.abort();
    };
  }, []);

  const runSearch = (raw) => {
    const trimmed = raw.trim();
    if (!trimmed) {
      setResults([]);
      setLoading(false);
      return;
    }
    if (controllerRef.current) controllerRef.current.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const reqId = ++reqRef.current;
    setLoading(true);
    // Unified search: hit the markets in play in parallel and merge (no pill).
    //
    // OUTSIDE a league this is Stocks + Crypto, exactly as before — the main
    // screen's behaviour is unchanged to the request. Inside one it is whatever
    // the organizer chose, so a stocks league never asks the crypto endpoint and
    // a forex league finally asks the forex one.
    const markets = game
      ? leagueSearchTabs(game)
      : ["Stocks", "Crypto"];
    Promise.allSettled(
      markets.map((category) => stockSearch({ category, query: trimmed }, controller.signal))
    ).then((settled) => {
      if (reqId !== reqRef.current) return; // a newer keystroke won
      const merged = [];
      for (const r of settled) {
        if (r.status === "fulfilled" && Array.isArray(r.value?.data)) merged.push(...r.value.data);
      }
      // Rank by closest match so the best result leads regardless of asset class
      // (searching "Tron" surfaces stock TRON and crypto TRON at the top, not all
      // stocks first with crypto buried at the bottom).
      // Closing the dead end. Restricting which markets are QUERIED is not
      // enough on its own: a custom-list league allows five stocks, not the
      // whole stock market, and the provider knows nothing about that. This is
      // the same question the Buy button asks, asked earlier.
      const allowed = merged.filter((item) =>
        isEligibleInLeague(game, item.ticker || item.symbol)
      );
      setResults(rankByMatch(allowed, trimmed));
      setLoading(false);
    });
  };

  const onChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(v), 350);
  };

  const pick = (item) => {
    const sym = item.ticker || item.symbol;
    if (!sym) return;
    // Open the chart (no watchlist add). Keep the raw prefixed symbol so crypto
    // charts resolve correctly. Dispatch tickerEvent so the chart swaps in place
    // (same listener the desktop search/wheel use) AND navigate to keep the URL
    // in sync — from /charts this remounts fresh, from /chartSearch it updates
    // in place.
    localStorage.setItem("ticker", sym);
    window.dispatchEvent(new CustomEvent("tickerEvent", { detail: { ticker: sym } }));
    // Client Rev 3, 3: "Inside a league, go to the Trade screen and use the
    // search icon in the header. When you search for an asset and select it, the
    // correct asset loads in the chart, but the user is now outside the league
    // environment. Please keep the user inside the active league."
    //
    // The chart swap was never the problem — the `tickerEvent` above is what
    // moves it, and /<slug>/trade listens for that event exactly like /charts
    // does. The navigate was: /chartSearch/<symbol> is a MAIN-APP address, so
    // picking a result walked the member out of the league shell and, with it,
    // out of the league's order ticket and portfolio context.
    //
    // Inside a league there is nowhere to navigate TO: the user is already on the
    // league's own Trade screen and the event has already loaded the asset there.
    // So the address only changes when this search is used from the main app.
    if (!leagueSlugFromPath(location.pathname)) {
      navigate(`/chartSearch/${sym}`);
    }
    onClose?.();
  };

  const showDropdown = loading || results.length > 0 || query.trim().length > 0;

  return (
    <div className="relative w-full">
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={onChange}
        placeholder="Search stocks & crypto"
        className="w-full rounded-full border border-gray-200 bg-slate-50 py-2 pl-9 pr-3 text-sm text-gray-800 focus:border-green-300 focus:outline-none"
      />
      <FontAwesomeIcon
        icon={faMagnifyingGlass}
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400"
      />

      {showDropdown && (
        <ul className="absolute left-0 top-full z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
          {loading ? (
            <li className="p-3 text-center text-sm italic text-gray-500">Searching…</li>
          ) : results.length === 0 ? (
            <li className="p-3 text-center text-sm text-gray-500">No matches found</li>
          ) : (
            results.map((item, i) => {
              const sym = item.ticker || item.symbol;
              return (
                <li
                  key={`${sym}-${i}`}
                  onClick={() => pick(item)}
                  className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-gray-50"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primaryColor text-[10px] font-semibold text-white">
                    {baseSymbol(sym)}
                  </span>
                  <span className="truncate text-sm text-gray-800">
                    {instrumentName({ name: item.name, symbol: sym })}
                  </span>
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}
